import './RemoveFromFavouritesModal.scss';

import trashCanIcon from '@ingka/ssr-icon/paths/trash-can';
import { FormEvent, useState } from 'react';

import { useLoading } from '../../../hooks/useLoading';
import ListView, { ListViewItem } from '@ingka/list-view';
import Button from '../../Skapa/Button/Button';
import Modal, { ModalBody, ModalFooter, ModalHeader, SheetSize, Sheets } from '../../Skapa/Modal/Modal';
import Text from '@ingka/text';
import { PREFIX } from '../../Skapa/constants/constants';

type Labels = {
  modal: {
    title: string;
    body: string;
  };
  footer: {
    remove: {
      label: string;
    };
  };
};
type List = {
  id: string;
  title: string;
  image: {
    alt?: string;
    src?: string;
    srcSet?: string;
    fallback?: boolean;
  };
};

enum LocalLoading {
  REMOVE = 'remove',
}
export interface RemoveFromFavouritesModalProps {
  isOpen: boolean;
  labels: Labels;
  lists: List[];
  onClose: () => void;
  onBack: () => void;
  onRemove: (lists: string[]) => Promise<void>;
  onRemoveCallback: (lists: string[], listName?: string) => void;
  onRemoveFallback: (error: any) => void;
}
export const RemoveFromFavouritesModal = (props: RemoveFromFavouritesModalProps) => {
  const { isOpen, labels, lists, onClose, onBack, onRemove, onRemoveCallback, onRemoveFallback } = props;
  const localLoading = useLoading<LocalLoading>([]);
  const [checkedLists, setCheckedLists] = useState<string[]>([]);

  const handleItemClick = (event: FormEvent<HTMLInputElement>, list: List) => {
    if (event.currentTarget.checked) {
      setCheckedLists((prev) => [...prev, list.id]);
    } else {
      setCheckedLists((prev) => prev.filter((listId) => listId !== list.id));
    }
  };

  return (
    <Modal
      visible={isOpen}
      handleCloseBtn={() => {
        setCheckedLists([]);
        onClose();
      }}
      scrollLock={false}
    >
      <Sheets
        header={
          <ModalHeader
            title={labels.modal.title}
            backBtnClick={() => {
              setCheckedLists([]);
              onBack();
            }}
          />
        }
        size={SheetSize.SMALL}
        footer={
          <ModalFooter>
            <Button
              ssrIcon={trashCanIcon}
              fluid={true}
              onClick={async () => {
                try {
                  localLoading.set(LocalLoading.REMOVE);
                  await onRemove(checkedLists);
                  onRemoveCallback(
                    checkedLists,
                    checkedLists.length === 1 ? lists.find((list) => list.id === checkedLists[0])?.title : undefined,
                  );
                } catch (error: any) {
                  onRemoveFallback(error);
                } finally {
                  setCheckedLists([]);
                  localLoading.unset(LocalLoading.REMOVE);
                }
              }}
              text={labels.footer.remove.label}
              loading={localLoading.check(LocalLoading.REMOVE)}
              disabled={checkedLists.length === 0}
            />
          </ModalFooter>
        }
      >
        <ModalBody>
          <Text tagName="h2" textStyle="Heading.L" className="remove-body">
            {labels.modal.body}
          </Text>
          <ListView prefix={PREFIX} id="remove-item-action-list">
            {lists.map((list) => (
              <ListViewItem
                prefix={PREFIX}
                emphasised
                key={list.id}
                title={list.title}
                control="checkbox"
                onChange={(event) => handleItemClick(event, list)}
                disabled={localLoading.check(LocalLoading.REMOVE)}
                image={list.image.src}
                imageAltText={list.image.alt ?? ''}
                imageProps={{ srcSet: list.image.srcSet, fallback: list.image.fallback }}
              />
            ))}
          </ListView>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};
